import { DateOp } from '../pipeline-ops'
import { MatchedSelect } from './MatchedSelect'

/**
 * Single-value filter rows for the sidebar, friendly for non-technical users:
 *   - NumericFilterRow     — a threshold ("Yearly revenue is at least $X").
 *   - DateCompareFilterRow — an absolute date bound ("Closed on or after X").
 *   - OpenDealsRow         — the is_open toggle as a removable chip.
 * Each owns a clear label + Remove, matching FilterSelect's layout.
 */

/** Shared text/number/date input styling for the scalar filter rows. */
const SCALAR_INPUT_CLASS =
  'h-9 w-full rounded-md border border-line bg-surface px-2 text-sm text-fg outline-none focus:border-line-focus'

/** Label + Remove header shared by every scalar filter row. */
function RowHeader({
  label,
  onRemove,
}: {
  label: string
  onRemove: () => void
}) {
  return (
    <div className="mb-1 flex items-center justify-between gap-2">
      <span className="text-xs text-fg-secondary">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-xs text-fg-secondary hover:text-error"
      >
        Remove
      </button>
    </div>
  )
}

export function NumericFilterRow({
  label,
  op,
  value,
  onChange,
  onRemove,
  ariaLabel,
}: {
  label: string
  op: 'gte' | 'lte'
  value: number
  onChange: (op: 'gte' | 'lte', value: number) => void
  onRemove: () => void
  ariaLabel: string
}) {
  return (
    <div className="flex min-w-44 flex-col">
      <RowHeader label={label} onRemove={onRemove} />
      <div className="flex items-center gap-2">
        <div className="w-32 shrink-0">
          <MatchedSelect
            ariaLabel={`${ariaLabel} comparison`}
            options={[
              { value: 'gte', label: 'At least' },
              { value: 'lte', label: 'At most' },
            ]}
            value={{
              value: op,
              label: op === 'gte' ? 'At least' : 'At most',
            }}
            onChange={(v) => onChange(v as 'gte' | 'lte', value)}
          />
        </div>
        <input
          type="number"
          inputMode="numeric"
          aria-label={ariaLabel}
          value={Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(op, Number(e.target.value))}
          className={SCALAR_INPUT_CLASS}
        />
      </div>
    </div>
  )
}

const DATE_OP_LABEL: Record<DateOp, string> = {
  date_after: 'On or after',
  date_before: 'On or before',
  within_days: 'In the last',
}

/**
 * Date filter — an operator plus a bound. Two flavors share the row:
 *   - absolute (on-or-after / on-or-before) + a calendar date, e.g.
 *     "Closed date is on or after 2026-01-01" (NumericFilterRow's gte/lte analogue).
 *   - rolling (in the last) + a day count, e.g. "Closed in the last 90 days".
 * `value` is a yyyy-mm-dd string for absolute ops, a day count for within_days.
 */
export function DateCompareFilterRow({
  label,
  op,
  value,
  onChange,
  onRemove,
  ariaLabel,
}: {
  label: string
  op: DateOp
  value: string | number
  onChange: (op: DateOp, value: string | number) => void
  onRemove: () => void
  ariaLabel: string
}) {
  // Switching to/from within_days swaps the value's meaning (day count vs date),
  // so reset to a sensible default for the new op rather than carry a stale value.
  const handleOpChange = (next: DateOp) => {
    if (next === op) return
    if (next === 'within_days') onChange(next, 90)
    else onChange(next, typeof value === 'string' ? value : '')
  }
  return (
    <div className="flex min-w-44 flex-col">
      <RowHeader label={label} onRemove={onRemove} />
      <div className="flex flex-col gap-2">
        <MatchedSelect
          ariaLabel={`${ariaLabel} comparison`}
          options={(Object.keys(DATE_OP_LABEL) as DateOp[]).map((o) => ({
            value: o,
            label: DATE_OP_LABEL[o],
          }))}
          value={{ value: op, label: DATE_OP_LABEL[op] }}
          onChange={(v) => handleOpChange(v as DateOp)}
        />
        {op === 'within_days' ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={0}
              aria-label={ariaLabel}
              // Blank for 0/empty so clearing (Number('') -> 0) doesn't stick a
              // "magic 0" in front of what you type; Number() drops leading zeros.
              value={!Number(value) ? '' : Number(value)}
              onChange={(e) => onChange(op, Number(e.target.value))}
              className={SCALAR_INPUT_CLASS}
            />
            <span className="shrink-0 text-sm text-fg-secondary">days</span>
          </div>
        ) : (
          <input
            type="date"
            aria-label={ariaLabel}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(op, e.target.value)}
            className={SCALAR_INPUT_CLASS}
          />
        )}
      </div>
    </div>
  )
}

export function OpenDealsRow({ onRemove }: { onRemove: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-line bg-surface px-3 py-2">
      <span className="text-sm text-fg">Open deals only</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-xs text-fg-secondary hover:text-error"
      >
        Remove
      </button>
    </div>
  )
}
